/**
 * Response Code Constants - CODE-ONLY PATTERN
 *
 * User-friendly messages for response codes from backend.
 * Backend sends ONLY codes (no messages) to minimize payload size.
 * Frontend owns all user-facing message copy.
 *
 * SYNCHRONIZATION:
 * Response codes must stay synced with backend:
 *   - Backend: apps/consumer/processors/responses/codes.go
 *   - Frontend: src/business/types/response-codes.types.ts
 *
 * See: src/business/constants/_README.md for full documentation
 */

import { ResponseCode } from '@/business/types/response-codes.types';

/**
 * User-friendly messages for each response code.
 * These messages should be:
 * - Clear and concise
 * - Action-oriented when possible
 * - Free of technical jargon
 * - Appropriate for end users
 *
 * IMPORTANT: Not all codes need user-facing messages.
 * Only codes that might be shown to users should have messages here.
 * Keep alphabetically sorted for easy maintenance.
 */
export const RESPONSE_CODE_MESSAGES: Partial<Record<ResponseCode, string>> = {
  ACCEPTED: 'Request accepted for processing',
  ACTION_NOT_SUPPORTED: 'This action is not supported',
  AUTH_CONTEXT_FAILED: 'Authentication context failed',
  BOARD_NOT_DELETABLE:
    'This board cannot be deleted as it is a default board assigned to your account.',
  BOARD_TAG_LIMIT_REACHED:
    'You have reached the maximum number of boards allowed for this type.',
  BOARD_TAG_NOT_ALLOWED_FOR_GROUP:
    'This board type cannot be assigned to a group.',
  BUSINESS_RULE_VIOLATION: 'This operation violates business rules',
  CARD_EDIT_LOCKED:
    'This card is in a locked column. Only pomodoro counts and reflection can be edited here.',
  CARD_FROZEN:
    'This card is from a past week and cannot be modified. Contact an admin if changes are needed.',
  COLUMN_CAPACITY_EXCEEDED:
    'This column is at maximum capacity. Please move or complete some cards before adding more.',
  CONFIRM_ON_EXIT_FIELD_REQUIRED:
    'A required field must be filled before the card can leave this column.',
  CONFIGURATION_ERROR: 'Configuration error. Please contact support.',
  CONFLICT: 'This operation conflicts with existing data',
  CREATED: 'Resource created successfully',
  DATABASE_ERROR: 'Database error. Please try again later.',
  DEPENDENCY_MISSING: 'Required dependency is missing',
  DUPLICATE_RESOURCE: 'This resource already exists',
  EXTERNAL_SERVICE_ERROR: 'External service error. Please try again later.',
  FACTORY_ERROR: 'Internal processing error',
  FORBIDDEN: 'You do not have permission to perform this action',
  GATEWAY_TIMEOUT: 'The request took too long to complete. Please try again.',
  INSUFFICIENT_PERMISSIONS:
    'You do not have sufficient permissions for this action',
  INTERNAL_ERROR: 'An unexpected error occurred. Please try again later.',
  JOB_ALREADY_EXISTS:
    'A job of this type already exists for this organization.',
  JOB_ALREADY_RUNNING:
    'This job is already running. Please wait for it to complete.',
  JOB_NOT_FOUND: 'The job was not found.',
  INTERNAL_SERVER_ERROR:
    'An unexpected error occurred. Please try again later.',
  INVALID_CARD_MOVEMENT:
    "This card movement is not allowed based on your board's workflow rules.",
  INVALID_INPUT:
    'Invalid input provided. Please check your data and try again.',
  INVALID_JSON: 'Invalid data format',
  INVALID_STATUS: 'Invalid status',
  IN_PROGRESS_LIMIT_EXCEEDED:
    "The 'In Progress' column can only hold 3 cards at a time. Please complete some tasks before starting new ones.",
  INVALID_CREDENTIALS: 'Please check your credentials and try again.',
  MISSING_REQUIRED_FIELD: 'Required field is missing. Please check your input.',
  NETWORK_ERROR:
    'Network connection problem. Please check your internet connection and try again.',
  NOT_FOUND: 'The requested resource was not found',
  NO_CONTENT: 'Operation completed',
  OPERATION_FAILED: 'The operation could not be completed. Please try again.',
  OPERATION_NOT_ALLOWED: 'This operation is not allowed',
  ORGANIZATION_CODE_ALREADY_EXISTS:
    'An organization with this code already exists. Please use a different code.',
  ORGANIZATION_TITLE_ALREADY_EXISTS:
    'An organization with this title already exists. Please use a different title.',
  PARTIAL_SUCCESS: 'Operation partially completed',
  PASSWORD_RESET_FAILED:
    'Something went wrong while resetting the password. Please try again.',
  PASSWORD_TOO_SHORT: 'Password must be at least 6 characters.',
  PROCESSING_FAILED: 'Processing failed. Please try again.',
  PROCESSOR_NOT_FOUND: 'Processor not found',
  QUOTA_EXCEEDED: 'Quota exceeded. Please contact support.',
  RATE_LIMIT_EXCEEDED: 'Too many requests. Please wait a moment and try again.',
  RATE_LIMITED: 'Too many requests. Please wait a moment and try again.',
  REQUEST_NOT_FOUND: 'Request not found',
  RESOURCE_LOCKED: 'This resource is currently locked. Please try again later.',
  RESOURCE_NOT_FOUND: 'The requested resource was not found',
  SERVICE_UNAVAILABLE:
    'Service is temporarily unavailable. Please try again later.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  START_DATE_IMMUTABLE:
    'The start date cannot be changed after card creation. Contact an admin to reschedule.',
  STUDENT_BACKWARD_MOVEMENT_BLOCKED:
    'You cannot move cards backward in your workflow. Please move cards forward or ask your mentor for help.',
  SUCCESS: 'Operation completed successfully',
  TIMEOUT_ERROR: 'The operation took too long to complete. Please try again.',
  UNAUTHORIZED: 'Please log in to continue',
  USERNAME_ALREADY_EXISTS: 'This username is already taken',
  USER_NOT_AUTHENTICATED: 'Please log in to continue',
  USER_NOT_AUTHORIZED: 'You do not have permission to perform this action',
  VALIDATION_FAILED:
    'Validation failed. Please check your input and try again.',
};

/**
 * Get user-friendly message for a response code.
 *
 * @param code The response code
 * @param fallback Optional fallback message if code not found
 * @returns User-friendly message
 */
export function getResponseCodeMessage(
  code: ResponseCode,
  fallback: string = 'An operation was performed'
): string {
  return RESPONSE_CODE_MESSAGES[code] ?? fallback;
}
